<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onMounted,
    onUnmounted,
    ref,
    watch,
  } from 'vue';
  import { useIntersectionObserver } from '@vueuse/core';
  import StatsService from '@/api/services/stats.service';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IStats } from '@/interfaces/IStats';
  import { IStatGroup } from '@/interfaces/IStatGroup';
  import { shortWeekdaysMonFirst } from '@/utils/intl-weekdays.utils';
  import {
    IBusinessDayAnchor,
    businessHourOrder,
    businessHourPosition,
    calendarWeekdayIndex,
  } from '@/domain/stats/business-day';
  import { getHeatmapRange } from '@/domain/dashboard/dashboard-timeframes';

  // Props
  const props = defineProps<{
    loading?: boolean;
    /** Branch business day resolved by the dashboard page (null = pending). */
    businessDay: IBusinessDayAnchor | null;
  }>();

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  // Services
  const statsService = new StatsService();
  const restaurantStore = useRestaurantStore();

  // Per `dayOfWeek-hour` cell data.
  interface IHeatmapCell {
    /** Drives color + primary tooltip line (weekly avg; ticket avg in 'average' mode). */
    display: number;
    /** Period sum backing the secondary line (0 in 'average' mode). */
    total: number;
    /** Divisor: weeks the weekday had data; 0 = none (and always 0 in 'average' mode). */
    weeks: number;
    /** Period comps sum (revenue/average modes; 0 otherwise). */
    comps: number;
  }

  // State
  const dataType = ref<'orders' | 'revenue' | 'average' | 'views'>('orders');
  const timeframe = ref<'7d' | '4w' | '3m'>('4w');
  const isLoading = ref(true);
  const hasLoadedOnce = ref(false);
  const chartContainerRef = ref<HTMLElement>();
  const isInView = ref(false);
  const canvasRef = ref<HTMLCanvasElement | null>(null);
  const heatmapData = ref<Map<string, IHeatmapCell>>(new Map());
  const maxValue = ref(0);
  // Business-day start hour the painted grid uses (columns run start hour
  // first, wrapping past midnight); committed with the data it belongs to.
  const axisCutoffHour = ref(0);
  // Latest load wins: slower stale responses never overwrite newer data.
  let loadSeq = 0;

  // Constants
  // Monday-first, matching the API's dayOfWeek indexing (0 = Monday).
  const dayNames = computed(() => shortWeekdaysMonFirst(proxy.$i18n.locale));
  const hourLabels = Array.from({ length: 24 }, (_, i) => i.toString());
  const CHART_PADDING = { top: 30, right: 20, bottom: 60, left: 60 };

  // Computed
  const timeframeInDays = computed(() => {
    switch (timeframe.value) {
      case '7d':
        return 7; // 7 days - 1 day to not include the same weekday twice
      case '4w':
        return 28; // 28 days - 1 day to not include the same weekday twice
      case '3m':
        // 12 complete weeks: every weekday×hour cell aggregates exactly 12
        // samples (90 days would over-sample some weekdays and skew cells).
        return 84;
      default:
        return 7;
    }
  });

  // Set up intersection observer
  const { stop } = useIntersectionObserver(
    chartContainerRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !hasLoadedOnce.value) {
        isInView.value = true;
        loadHeatmapData();
        stop(); // Stop observing once loaded
      }
    },
    {
      threshold: 0.1, // Trigger when 10% of the element is visible
    }
  );

  // Methods
  const loadHeatmapData = async () => {
    if (!isInView.value) return; // Don't load if not in view
    const anchor = props.businessDay;
    // The page is still resolving the business day; its watcher loads later.
    if (!anchor) return;
    const seq = ++loadSeq;

    isLoading.value = true;
    hasLoadedOnce.value = true;
    // Reset cells up-front so a failed load or an early return in
    // processHeatmapData can't leave stale data from another mode.
    heatmapData.value = new Map();
    axisCutoffHour.value = anchor.cutoffHour;

    try {
      // The business days that end yesterday.
      const { from, to } = getHeatmapRange(timeframeInDays.value, anchor.today);

      let stats: IStats;

      if (
        dataType.value === 'orders' ||
        dataType.value === 'revenue' ||
        dataType.value === 'average'
      ) {
        stats = await statsService.getOrdersStats(
          anchor.restaurantId,
          anchor.branchId,
          from,
          to,
          undefined, // orderTypeIds
          'weekly',
          'weekly',
          dataType.value === 'revenue' || dataType.value === 'average'
        );
      } else {
        stats = await statsService.getViewsStats(
          anchor.restaurantId,
          anchor.branchId,
          from,
          to,
          'weekly',
          'weekly'
        );
      }
      // A newer load started while this one was in flight.
      if (seq !== loadSeq) return;

      // Process the heatmap data
      processHeatmapData(stats);
    } catch (error) {
      if (seq === loadSeq) console.error('Error loading heatmap data:', error);
    } finally {
      // Every current load (success, empty or failed) repaints the grid
      // with its own axis; no-op while the canvas is not rendered yet.
      if (seq === loadSeq) {
        isLoading.value = false;
        drawHeatmap();
      }
    }
  };

  const processHeatmapData = (stats: IStats) => {
    if (!stats.groups || stats.groups.length === 0) return;

    const calendarWeeks = timeframeInDays.value / 7;
    const dataMap = new Map<string, IHeatmapCell>();
    let max = 0;

    // Non-monetary comps per cell (invitaciones / consumo empleado), only
    // requested for revenue/average. Looked up BY ID, never by index.
    const compsMap = new Map<string, number>();
    if (dataType.value === 'revenue' || dataType.value === 'average') {
      const compsGroup = stats.groups.find((g) => g.id === 'nonMonetaryTotal');
      for (const stat of compsGroup?.stats || []) {
        compsMap.set(`${stat.dayOfWeek}-${stat.hour}`, stat.value);
      }
    }

    // Handle average ticket calculation
    if (dataType.value === 'average') {
      const countGroup = stats.groups[0]; // orders count
      const totalGroup = stats.groups[1]; // revenue total

      if (!countGroup || !totalGroup) return;

      const countMap = new Map<string, number>();
      for (const stat of countGroup.stats) {
        countMap.set(`${stat.dayOfWeek}-${stat.hour}`, stat.value);
      }

      // NET ticket averages: comped amounts are excluded from the total,
      // but comped orders stay in the count (deliberate semantics). Weeks
      // are irrelevant for a per-order average.
      for (const stat of totalGroup.stats) {
        const key = `${stat.dayOfWeek}-${stat.hour}`;
        const count = countMap.get(key) || 0;
        const comps = compsMap.get(key) || 0;
        const netTotal = Math.max(0, stat.value - comps);
        const average = count > 0 ? netTotal / count : 0;
        dataMap.set(key, { display: average, total: 0, weeks: 0, comps });
        if (average > max) max = average;
      }
    } else {
      // Orders/views use the first group, revenue the 'total' group.
      const group = stats.groups[dataType.value === 'revenue' ? 1 : 0];
      if (!group) return;

      // Weekly average per cell: divide the (net) sum by the distinct weeks
      // in which the cell's WEEKDAY had any data (API-provided). Day-level
      // so quiet hours count as zeros whenever the venue operated that day,
      // while weeks it didn't (not yet open, vacation, closing day) don't
      // dilute. Falls back to calendar weeks if the API doesn't send it.
      for (const stat of group.stats) {
        const key = `${stat.dayOfWeek}-${stat.hour}`;
        const comps =
          dataType.value === 'revenue' ? compsMap.get(key) || 0 : 0;
        const netTotal =
          dataType.value === 'revenue'
            ? Math.max(0, stat.value - comps)
            : stat.value;
        const weeks = stat.weeks ?? calendarWeeks;
        const display = weeks > 0 ? netTotal / weeks : 0;
        dataMap.set(key, { display, total: netTotal, weeks, comps });
        if (display > max) max = display;
      }
    }

    heatmapData.value = dataMap;
    maxValue.value = max;
    // Drawn by loadHeatmapData once the load settles.
  };

  const getColor = (value: number): string => {
    if (maxValue.value === 0 || value === 0) return 'rgba(255, 111, 0, 0.05)';

    const intensity = value / maxValue.value;
    // Orange gradient from light to dark
    const alpha = 0.1 + intensity * 0.9; // 0.1 to 1.0
    return `rgba(255, 111, 0, ${alpha})`;
  };

  const drawHeatmap = () => {
    if (!canvasRef.value) return;

    const canvas = canvasRef.value;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Calculate cell dimensions
    const chartWidth = canvas.width - CHART_PADDING.left - CHART_PADDING.right;
    const chartHeight =
      canvas.height - CHART_PADDING.top - CHART_PADDING.bottom;
    const cellWidth = chartWidth / 24;
    const cellHeight = chartHeight / 7;

    // Columns follow the business day: start hour first, wrapping past
    // midnight (the labels keep the real clock hour).
    const hours = businessHourOrder(axisCutoffHour.value);

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw cells
    for (let day = 0; day < 7; day++) {
      for (let col = 0; col < 24; col++) {
        const key = `${day}-${hours[col]}`;
        const value = heatmapData.value.get(key)?.display || 0;
        const x = CHART_PADDING.left + col * cellWidth;
        const y = CHART_PADDING.top + day * cellHeight;

        // Draw cell
        ctx.fillStyle = getColor(value);
        ctx.fillRect(x, y, cellWidth - 1, cellHeight - 1);

        // Draw border
        ctx.strokeStyle = 'rgba(255, 111, 0, 0.2)';
        ctx.strokeRect(x, y, cellWidth - 1, cellHeight - 1);
      }
    }

    // Midnight marker (only when the business day wraps past midnight)
    if (axisCutoffHour.value > 0) {
      const midnightX =
        CHART_PADDING.left +
        businessHourPosition(0, axisCutoffHour.value) * cellWidth -
        1;
      ctx.save();
      ctx.strokeStyle = '#a3a3a3';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(midnightX, CHART_PADDING.top - 4);
      ctx.lineTo(midnightX, CHART_PADDING.top + chartHeight + 4);
      ctx.stroke();
      ctx.restore();
    }

    // Draw labels
    ctx.fillStyle = '#666';
    ctx.font = '12px Arial';

    // Y-axis labels (days)
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let day = 0; day < 7; day++) {
      const y = CHART_PADDING.top + day * cellHeight + cellHeight / 2;
      ctx.fillText(dayNames.value[day], CHART_PADDING.left - 10, y);
    }

    // X-axis labels (hours)
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (let col = 0; col < 24; col++) {
      const x = CHART_PADDING.left + col * cellWidth + cellWidth / 2;
      ctx.fillText(
        hours[col].toString(),
        x,
        CHART_PADDING.top + chartHeight + 10
      );
    }

    // Axis labels
    ctx.font = '14px Arial';
    ctx.fillStyle = '#333';

    // X-axis label
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(
      String(proxy.$t('dashboard.hourOfDay')),
      canvas.width / 2,
      canvas.height - 15
    );

    // Y-axis label (rotated)
    ctx.save();
    ctx.translate(15, canvas.height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(proxy.$t('dashboard.dayOfWeek')), 0, 0);
    ctx.restore();
  };

  // Canvas mouse events for tooltips
  const tooltipData = ref<{
    x: number;
    y: number;
    text: string;
    totalText: string | null;
    compsText: string | null;
  } | null>(null);

  /** 1-decimal count, trailing .0 stripped (3 → "3", 3.25 → "3.3"). */
  const fmtCount = (value: number): string =>
    value.toFixed(1).replace(/\.0$/, '');

  const handleMouseMove = (event: MouseEvent) => {
    if (!canvasRef.value || isLoading.value) return;

    const rect = canvasRef.value.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const chartWidth = rect.width - CHART_PADDING.left - CHART_PADDING.right;
    const chartHeight = rect.height - CHART_PADDING.top - CHART_PADDING.bottom;
    const cellWidth = chartWidth / 24;
    const cellHeight = chartHeight / 7;

    // Check if mouse is in chart area
    if (
      x >= CHART_PADDING.left &&
      x <= rect.width - CHART_PADDING.right &&
      y >= CHART_PADDING.top &&
      y <= rect.height - CHART_PADDING.bottom
    ) {
      const col = Math.floor((x - CHART_PADDING.left) / cellWidth);
      const day = Math.floor((y - CHART_PADDING.top) / cellHeight);

      if (col >= 0 && col < 24 && day >= 0 && day < 7) {
        // Column → real clock hour along the business-day axis.
        const hour = businessHourOrder(axisCutoffHour.value)[col];
        const key = `${day}-${hour}`;
        const cell = heatmapData.value.get(key);
        const display = cell?.display || 0;
        const weeks = cell?.weeks || 0;
        const sym = restaurantStore.currency?.symbol || '$';
        const label = String(
          proxy.$t(
            dataType.value === 'orders'
              ? 'dashboard.unitOrders'
              : 'dashboard.unitViews'
          )
        );
        const perWeek = String(proxy.$t('dashboard.perWeekSuffix'));

        // Primary line: weekly average shown as a plain value ('average'
        // mode is a per-order ticket value). The secondary "Total: X en Y
        // semanas" line conveys the per-week semantics.
        let formattedValue = '';
        if (dataType.value === 'revenue' || dataType.value === 'average') {
          formattedValue = `${sym}${display.toFixed(2)}`;
        } else {
          formattedValue = `${fmtCount(display)} ${label}`;
        }

        // Secondary line: period total, only for cells that actually have
        // data across >1 week (day-level weeks make empty cells carry the
        // weekday's count, and "Total: 0" is noise).
        let totalText: string | null = null;
        if (cell && cell.total > 0 && weeks > 1) {
          const amount =
            dataType.value === 'revenue'
              ? `${sym}${cell.total.toFixed(2)}`
              : `${cell.total} ${label}`;
          totalText = String(
            proxy.$t('dashboard.heatmapPeriodTotal', { amount, weeks })
          );
        }

        // Excluded non-monetary amounts: per-week in revenue mode (matches
        // the primary average), period total in ticket-average mode.
        let compsText: string | null = null;
        const comps = cell?.comps || 0;
        if (comps > 0) {
          const compsAmount =
            dataType.value === 'revenue'
              ? `${sym}${(comps / Math.max(1, weeks)).toFixed(2)}${perWeek}`
              : `${sym}${comps.toFixed(2)}`;
          compsText = String(
            proxy.$t('dashboard.nonMonetaryTooltipLine', {
              amount: compsAmount,
            })
          );
        }

        // Calculate center of the hovered cell
        const cellCenterX =
          CHART_PADDING.left + col * cellWidth + cellWidth / 2;
        const cellCenterY =
          CHART_PADDING.top + day * cellHeight + cellHeight / 2;

        // Rows are business weekdays; name the calendar day of the hour
        // (after midnight it is already the next day: "mar 0:00").
        const calendarDay = calendarWeekdayIndex(
          day,
          hour,
          axisCutoffHour.value
        );

        tooltipData.value = {
          x: cellCenterX,
          y: cellCenterY,
          text: `${dayNames.value[calendarDay]} ${hour}:00 - ${formattedValue}`,
          totalText,
          compsText,
        };
        return;
      }
    }

    tooltipData.value = null;
  };

  const handleMouseLeave = () => {
    tooltipData.value = null;
  };

  // Watchers (a new business day = branch switch or page re-anchor)
  watch([dataType, timeframe, () => props.businessDay], () => {
    if (isInView.value) {
      loadHeatmapData();
    }
  });

  // Axis/day labels are painted on the canvas, so a locale switch must
  // trigger a redraw (data itself is locale-independent).
  watch(
    () => proxy.$i18n.locale,
    () => {
      if (hasLoadedOnce.value && !isLoading.value) {
        drawHeatmap();
      }
    }
  );

  // Lifecycle
  onMounted(() => {
    // Remove loadHeatmapData() from here - it will be triggered by intersection observer
    window.addEventListener('resize', drawHeatmap);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', drawHeatmap);
  });
</script>

<template>
  <v-card class="heatmap-card" elevation="0">
    <v-card-title class="card-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon">mdi-calendar-clock</v-icon>
          <span class="header-title">{{ $t('dashboard.activityMap') }}</span>
        </div>
      </div>
    </v-card-title>
    <v-card-text class="card-content">
      <!-- Header Controls -->
      <div class="header-controls">
        <!-- Data Type Toggle -->
        <v-btn-toggle v-model="dataType" mandatory dense class="data-toggle">
          <v-btn value="orders" small>
            <v-icon left small class="toggle-button-icon">mdi-receipt</v-icon>
            {{ $t('dashboard.metricOrders') }}
          </v-btn>
          <v-btn value="revenue" small>
            <v-icon left small class="toggle-button-icon"
              >mdi-cash-multiple</v-icon
            >
            {{ $t('dashboard.metricRevenue') }}
          </v-btn>
          <v-btn value="average" small>
            <v-icon left small class="toggle-button-icon"
              >mdi-calculator</v-icon
            >
            <span class="sm:tw-hidden">
              {{ $t('dashboard.averageTicketShort') }}
            </span>
            <span class="tw-hidden sm:tw-block">
              {{ $t('dashboard.averageTicket') }}
            </span>
          </v-btn>
          <v-btn value="views" small>
            <v-icon left small class="toggle-button-icon">mdi-eye</v-icon>
            {{ $t('dashboard.metricViews') }}
          </v-btn>
        </v-btn-toggle>

        <!-- Timeframe Selector -->
        <v-btn-toggle
          v-model="timeframe"
          mandatory
          dense
          class="timeframe-toggle"
        >
          <v-btn value="7d" small>{{ $t('dashboard.tf7d') }}</v-btn>
          <v-btn value="4w" small>{{ $t('dashboard.tf4w') }}</v-btn>
          <v-btn value="3m" small>{{ $t('dashboard.tf12w') }}</v-btn>
        </v-btn-toggle>
      </div>

      <!-- Chart Container -->
      <div class="chart-container" ref="chartContainerRef">
        <!-- Skeleton loader when not loaded -->
        <div v-if="!hasLoadedOnce" class="skeleton-container">
          <v-skeleton-loader type="image" height="100%" width="100%" />
        </div>

        <!-- Actual content - shown after first load -->
        <template v-else>
          <!-- Loading State -->
          <div v-if="isLoading" class="loading-overlay">
            <v-progress-circular indeterminate color="primary" :size="60" />
            <p class="loading-text">{{ $t('dashboard.loadingData') }}</p>
          </div>

          <!-- Canvas -->
          <canvas
            ref="canvasRef"
            :class="{ 'is-loading': isLoading }"
            @mousemove="handleMouseMove"
            @mouseleave="handleMouseLeave"
          />

          <!-- Tooltip -->
          <div
            v-if="tooltipData"
            class="heatmap-tooltip"
            :style="{
              left: tooltipData.x + 'px',
              top: tooltipData.y + 'px',
            }"
          >
            <div class="tooltip-text">{{ tooltipData.text }}</div>
            <div v-if="tooltipData.totalText" class="tooltip-total">
              {{ tooltipData.totalText }}
            </div>
            <div v-if="tooltipData.compsText" class="tooltip-comps">
              {{ tooltipData.compsText }}
            </div>
          </div>
        </template>
      </div>

      <!-- Legend - Always visible -->
      <div class="heatmap-legend">
        <span class="legend-label">{{ $t('dashboard.legendLess') }}</span>
        <div class="legend-gradient"></div>
        <span class="legend-label">{{ $t('dashboard.legendMore') }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .heatmap-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;
    @apply tw-w-full tw-overflow-hidden;

    .card-header {
      @apply tw-pb-2 tw-pt-3;

      .header-content {
        @apply tw-flex tw-items-center tw-justify-between tw-w-full;

        .header-left {
          @apply tw-flex tw-items-center tw-gap-2;

          .header-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
          }

          .header-title {
            @apply tw-text-base tw-font-semibold tw-text-neutral-800;
          }
        }
      }
    }

    .card-content {
      @apply tw-pt-0;

      .header-controls {
        @apply tw-flex tw-items-center tw-justify-between;
        @apply tw-flex-wrap tw-gap-3 tw-mb-4;

        .data-toggle,
        .timeframe-toggle {
          @apply tw-bg-neutral-100 tw-rounded;

          .toggle-button-icon {
            @apply tw-hidden md:tw-block #{!important};
          }
        }
      }

      .chart-container {
        @apply tw-relative tw-w-full;
        @apply tw-h-64 sm:tw-h-80 lg:tw-h-96;
        @apply tw-mb-4;

        .skeleton-container {
          @apply tw-w-full tw-h-full;
          @apply tw-flex tw-items-center tw-justify-center;
        }

        canvas {
          @apply tw-w-full tw-h-full;
          @apply tw-transition-opacity tw-duration-300;
          @apply tw-cursor-crosshair;

          &.is-loading {
            @apply tw-opacity-20;
          }
        }

        .loading-overlay {
          @apply tw-absolute tw-inset-0;
          @apply tw-flex tw-flex-col tw-items-center tw-justify-center;
          @apply tw-bg-white tw-bg-opacity-90;
          @apply tw-rounded;

          .loading-text {
            @apply tw-mt-2 tw-text-neutral-600;
          }
        }

        .heatmap-tooltip {
          position: absolute;
          @apply tw-z-50;
          @apply tw-bg-neutral-900 tw-text-white;
          @apply tw-px-3 tw-py-2 tw-rounded;
          @apply tw-text-sm tw-whitespace-nowrap;
          @apply tw-pointer-events-none;
          transform: translateX(-50%) translateY(-100%);
          margin-top: -8px;

          .tooltip-total {
            // Muted period total on the dark tooltip background
            @apply tw-text-neutral-400;
          }

          .tooltip-comps {
            // Light amber for contrast on the dark tooltip background
            @apply tw-text-amber-300;
          }

          // Add arrow pointing down
          &::after {
            content: '';
            @apply tw-absolute;
            @apply tw-border-4 tw-border-transparent;
            @apply tw-border-t-neutral-900;
            left: 50%;
            transform: translateX(-50%);
            top: 100%;
          }
        }
      }

      .heatmap-legend {
        @apply tw-flex tw-items-center tw-justify-center tw-gap-2;
        @apply tw-text-sm tw-text-neutral-600;

        .legend-gradient {
          @apply tw-h-3 tw-w-32 tw-rounded;
          background: linear-gradient(
            to right,
            rgba(255, 111, 0, 0.05),
            rgba(255, 111, 0, 1)
          );
        }
      }

      // Mobile optimizations
      @media (max-width: 640px) {
        .header-controls {
          @apply tw-w-full;
          @apply tw-justify-center;
        }

        .chart-container {
          // Allow horizontal scroll on mobile for better visibility
          @apply tw-overflow-x-auto;
          -webkit-overflow-scrolling: touch;

          canvas {
            @apply tw-min-w-full;
            min-width: 600px; // Ensure minimum width for readability
          }
        }
      }
    }
  }
</style>
